import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, doc, getDoc, 
  addDoc, updateDoc, Timestamp 
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { 
  IoTimerOutline, IoLocationOutline, IoCheckmarkCircleOutline 
} from 'react-icons/io5';
import './TrabajadorDashboardScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function TrabajadorAsistenciaScreen() {
  const { currentUser, userData } = useAuth();
  
  const [asistenciaHoy, setAsistenciaHoy] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');

  useEffect(() => {
    if (!currentUser || !userData?.condominioId) return;

    const hoyStr = new Date().toISOString().split('T')[0];
    const qAsistencia = query(
      collection(db, "asistencia_trabajadores"),
      where("trabajadorId", "==", currentUser.uid),
      where("fecha", "==", hoyStr)
    );
    return onSnapshot(qAsistencia, (snap) => {
      if (!snap.empty) {
        setAsistenciaHoy({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } else {
        setAsistenciaHoy(null);
      }
    });
  }, [currentUser, userData]);

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // metres
    const phi1 = lat1 * Math.PI/180;
    const phi2 = lat2 * Math.PI/180;
    const deltaPhi = (lat2-lat1) * Math.PI/180;
    const deltaLambda = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(deltaPhi/2) * Math.sin(deltaPhi/2) +
              Math.cos(phi1) * Math.cos(phi2) *
              Math.sin(deltaLambda/2) * Math.sin(deltaLambda/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  };

  const handleCheckInOut = async () => {
    setGpsLoading(true);
    setGpsError('');

    if (!navigator.geolocation) {
      setGpsError('La geolocalización no está soportada por tu navegador.');
      setGpsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;

      try {
        const isSaaSOrGlobal = userData.condominioId === 'GLOBAL' || userData.condominioId === 'SAAS';
        
        if (!isSaaSOrGlobal) {
          const condoSnap = await getDoc(doc(db, "condominios", userData.condominioId));
          if (!condoSnap.exists()) {
            throw new Error("No se pudo obtener la ubicación de tu condominio.");
          }
          
          const condoData = condoSnap.data();
          const condoLocation = condoData.location || condoData.coordenadas;
          
          if (condoLocation && condoLocation.lat && condoLocation.lng) {
            const distance = getDistance(latitude, longitude, condoLocation.lat, condoLocation.lng);
            if (distance > 300) {
              throw new Error(`Estás demasiado lejos del fraccionamiento (${Math.round(distance)}m). Debes estar dentro de la zona de trabajo.`);
            }
          }
        }

        const hoyStr = new Date().toISOString().split('T')[0];

        if (!asistenciaHoy) {
          await addDoc(collection(db, "asistencia_trabajadores"), {
            trabajadorId: currentUser.uid,
            trabajadorNombre: `${userData.nombre} ${userData.apellidos || ''}`.trim(),
            condominioId: userData.condominioId,
            fecha: hoyStr,
            checkIn: Timestamp.now(),
            checkInCoords: { lat: latitude, lng: longitude },
            estado: 'activo'
          });
        } else {
          await updateDoc(doc(db, "asistencia_trabajadores", asistenciaHoy.id), {
            checkOut: Timestamp.now(),
            checkOutCoords: { lat: latitude, lng: longitude },
            estado: 'completado'
          });
        }

      } catch (err) {
        setGpsError(err.message);
      } finally {
        setGpsLoading(false);
      }
    }, (error) => {
      console.error(error);
      setGpsError('No se pudo acceder a tu ubicación GPS. Activa la ubicación en tu dispositivo.');
      setGpsLoading(false);
    }, { enableHighAccuracy: true, timeout: 10000 });
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/trabajador/asistencia" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />
        <KondoBrandHeader showBack={false} />

        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Asistencia</h1>
                <p>Control de asistencia y GPS</p>
              </div>
              <IoTimerOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div style={{ padding: '16px' }}>
          <div className="trabajador-card-premium checador-card" style={{ maxWidth: '600px', margin: '20px auto' }}>
            <div className="checador-header">
              <IoTimerOutline size={32} className="checador-icon" />
              <div>
                <h3 style={{ margin: '0 0 6px 0', fontSize: '1.4rem' }}>Control de Asistencia</h3>
                <p>Registra tu entrada y salida dentro del fraccionamiento</p>
              </div>
            </div>

            {gpsError && (
              <div className="checador-error-banner">
                <IoLocationOutline size={16} />
                <span>{gpsError}</span>
              </div>
            )}

            <div className="checador-status-indicators">
              <div className={`indicator-pill ${asistenciaHoy ? 'success' : 'pending'}`}>
                <span>Check-In:</span>
                <strong>
                  {asistenciaHoy?.checkIn 
                    ? new Date(asistenciaHoy.checkIn.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Pendiente'
                  }
                </strong>
              </div>
              <div className={`indicator-pill ${asistenciaHoy?.checkOut ? 'success' : 'pending'}`}>
                <span>Check-Out:</span>
                <strong>
                  {asistenciaHoy?.checkOut 
                    ? new Date(asistenciaHoy.checkOut.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Pendiente'
                  }
                </strong>
              </div>
            </div>

            <button 
              onClick={handleCheckInOut} 
              disabled={gpsLoading || asistenciaHoy?.estado === 'completado'}
              className={`checador-btn ${asistenciaHoy ? 'checkout' : 'checkin'}`}
            >
              {gpsLoading ? 'Validando ubicación...' : (asistenciaHoy ? 'Registrar Salida' : 'Registrar Entrada')}
            </button>
          </div>
        </div>
      </div>
    </main>

      <KondoBottomNav activeRoute="/trabajador/asistencia" />
    </div>
  );
}

export default TrabajadorAsistenciaScreen;
